import type { ReactNode } from "react";
import { BrandLogo, companyName } from "../../components/BrandLogo";

interface EOCFormLayoutProps {
  title: string;
  subtitle: string;
  children: ReactNode;
}

export function EOCFormLayout({ title, subtitle, children }: EOCFormLayoutProps) {
  return (
    <div className="min-h-screen bg-[var(--brand-bg-dark)] flex flex-col">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-[var(--brand-gradient-start)]/20 to-[var(--brand-gradient-end)]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/4 -left-40 w-[600px] h-[600px] bg-gradient-to-tr from-[var(--brand-gradient-end)]/15 to-[var(--brand-gradient-start)]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/4 w-[400px] h-[400px] bg-gradient-to-tl from-[var(--brand-primary)]/10 to-[var(--brand-accent)]/5 rounded-full blur-3xl" />
      </div>

      <header className="relative z-10 py-6 px-4 sm:px-8">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-3">
          <BrandLogo variant="primary" className="h-10 w-auto" />
          <span className="text-xl font-bold text-white">{companyName}</span>
        </div>
      </header>

      <main className="relative z-10 flex-1 py-6 px-4 sm:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{title}</h1>
            <p className="text-white/60">{subtitle}</p>
          </div>
          <div className="bg-[var(--brand-card-dark)]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-6 sm:p-8 shadow-2xl">
            {children}
          </div>
        </div>
      </main>

      <footer className="relative z-10 py-6 px-4">
        <p className="text-center text-xs text-white/40">
          © {new Date().getFullYear()} {companyName}. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export function FormField({ label, required, children, hint }: { label: string; required?: boolean; children: ReactNode; hint?: string }) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-white/90">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-white/50">{hint}</p>}
    </div>
  );
}

export function TextInput({ type = "text", value, onChange, placeholder, required, min, step }: {
  type?: "text" | "number" | "date"; value: string | number; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; min?: number; step?: number;
}) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      required={required} min={min} step={step}
      className="w-full px-4 py-3 rounded-xl bg-[#1f1f2e] border-0 text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-50" />
  );
}

export function TextArea({ value, onChange, placeholder, required, rows = 3 }: {
  value: string; onChange: (v: string) => void; placeholder?: string; required?: boolean; rows?: number;
}) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      required={required} rows={rows}
      className="w-full px-4 py-3 rounded-xl bg-[#1f1f2e] border-0 text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all resize-none" />
  );
}

export function Select({ value, onChange, options, placeholder, required }: {
  value: string; onChange: (v: string) => void; options: { value: string; label: string }[];
  placeholder?: string; required?: boolean;
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} required={required}
      className="w-full px-4 py-3 rounded-xl bg-[#1f1f2e] border-0 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all appearance-none cursor-pointer"
      style={{ colorScheme: "dark" }}>
      {placeholder && <option value="" disabled className="text-white/40">{placeholder}</option>}
      {options.map(opt => <option key={opt.value} value={opt.value} className="bg-[#1f1f2e] text-white">{opt.label}</option>)}
    </select>
  );
}
